import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useProfile } from '../hooks/useProfile';
import { useTranslation } from 'react-i18next';
import { Title } from '../components/ui/Title';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { DateTimePicker } from '../components/DateTimePicker';
import { LocationAutocomplete } from '../components/LocationAutocomplete';
import { BandMultiSelect } from '../components/filters/BandMultiSelect';
import type { Band } from '../types/database';

const createEventSchema = z.object({
  title: z.string().min(3).max(100),
  description: z.string().min(10).optional(),
  location: z.string().min(3),
  coordinates: z.object({
    latitude: z.number(),
    longitude: z.number(),
  }),
  event_date: z.date().min(new Date(), { message: "Event date must be in the future" }),
});

type CreateEventFormData = z.infer<typeof createEventSchema>;

function CreateEvent() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { bandSlug } = useParams<{ bandSlug?: string }>();
  const { user } = useAuth();
  const { profile } = useProfile();
  const [error, setError] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [bands, setBands] = React.useState<Band[]>([]);
  const [selectedBands, setSelectedBands] = React.useState<Band[]>([]);
  const [bandsError, setBandsError] = React.useState('');

  const { register, handleSubmit, formState: { errors }, setValue, watch } = useForm<CreateEventFormData>({
    resolver: zodResolver(createEventSchema),
    defaultValues: {
      event_date: null,
      coordinates: null,
    },
  });

  React.useEffect(() => {
    if (profile) {
      fetchBands();
    }
  }, [bandSlug, profile]);

  const fetchBands = async () => {
    if (!profile) return;

    try {
      const { data, error } = await supabase
        .from('bands')
        .select('*')
        .or(`is_claimed.eq.true,created_by.eq.${profile.id}`)
        .order('name');

      if (error) throw error;
      setBands(data || []);

      if (bandSlug && data) {
        const preselectedBand = data.find(b => b.slug === bandSlug);
        if (preselectedBand) {
          setSelectedBands([preselectedBand]);
        }
      }
    } catch (err) {
      // Ignore error silently
    }
  };

  const handleInviteBand = async (bandName: string, email: string) => {
    if (!profile) {
      throw new Error('You must be logged in');
    }

    const slug = bandName.toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();

    const { data: band, error: bandError } = await supabase
      .from('bands')
      .insert({
        name: bandName,
        slug: `${slug}-${Date.now()}`,
        is_claimed: false,
        status: 'Aktiv',
        created_by: profile.id,
      })
      .select()
      .single();

    if (bandError) {
      throw bandError;
    }

    const { data: functionData, error: functionError } = await supabase.functions.invoke('send-band-claim-invitation', {
      body: {
        email,
        bandId: band.id,
      },
    });

    if (functionError) {
      throw functionError;
    }

    if (functionData?.error) {
      throw new Error(functionData.error);
    }

    setSelectedBands([...selectedBands, band]);
    setBands([...bands, band]);
  };

  const onSubmit = async (data: CreateEventFormData) => {
    if (isSubmitting) return;

    if (!user || !profile) {
      setError('You must be logged in to create an event');
      return;
    }

    if (selectedBands.length === 0) {
      setBandsError('Bitte wähle mindestens eine Band aus');
      return;
    }

    try {
      setIsSubmitting(true);
      setBandsError('');

      const { data: event, error: eventError } = await supabase
        .from('events')
        .insert([
          {
            ...data,
            created_by: profile.id,
          },
        ])
        .select()
        .single();

      if (eventError) throw eventError;

      const eventBands = selectedBands.map(band => ({
        event_id: event.id,
        band_id: band.id,
      }));

      const { error: eventBandsError } = await supabase
        .from('event_bands')
        .insert(eventBands);

      if (eventBandsError) throw eventBandsError;

      navigate('/agenda');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create event');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLocationChange = (place: { name: string; coordinates: { latitude: number; longitude: number } }) => {
    setValue('location', place.name);
    setValue('coordinates', place.coordinates);
  };

  const breadcrumbItems = [
    { label: t('nav.agenda'), href: '/agenda' },
    { label: t('events.createEvent.title') },
  ];

  return (
    <div className="container mx-auto px-6 pb-8 md:py-24">
      <div className="mb-6">
        <Breadcrumbs items={breadcrumbItems} />
      </div>

      <Title subtitle={t('events.createEvent.subtitle')}>
        {t('events.createEvent.title')}
      </Title>

      <div className="max-w-4xl mx-auto">
        {error && (
          <div className="bg-red-500/10 text-red-500 p-4 rounded-lg mb-6 text-center">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
          <div className="panel">
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-light/80 mb-2">
                  Bands
                </label>
                <BandMultiSelect
                  bands={bands}
                  selectedBands={selectedBands}
                  onSelectBands={setSelectedBands}
                  onInviteBand={handleInviteBand}
                  placeholder="Wähle Bands aus..."
                  error={bandsError}
                />
                <p className="mt-1 text-sm text-light/60">
                  Du kannst mehrere Bands auswählen oder eine noch nicht registrierte Band per E-Mail einladen
                </p>
              </div>

              <div>
                <label htmlFor="title" className="block text-sm font-medium text-light/80 mb-2">
                  {t('events.form.title')}
                </label>
                <input
                  type="text"
                  id="title"
                  {...register('title')}
                  placeholder={t('forms.enterEventTitle')}
                  className="mt-1 block w-full rounded-lg bg-dark border border-gray-custom/20 px-4 py-2 text-light focus:border-gray-custom/40 focus:outline-none"
                />
                {errors.title && (
                  <p className="mt-1 text-sm text-red-500">{errors.title.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="description" className="block text-sm font-medium text-light/80 mb-2">
                  {t('events.form.description')}
                </label>
                <textarea
                  id="description"
                  rows={4}
                  {...register('description')}
                  placeholder={t('forms.enterEventDescription')}
                  className="mt-1 block w-full rounded-lg bg-dark border border-gray-custom/20 px-4 py-2 text-light focus:border-gray-custom/40 focus:outline-none"
                />
                {errors.description && (
                  <p className="mt-1 text-sm text-red-500">{errors.description.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-light/80 mb-2">
                  {t('events.form.location')}
                </label>
                <LocationAutocomplete
                  value={watch('location') || ''}
                  onChange={handleLocationChange}
                  error={errors.location?.message}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-light/80 mb-2">
                  {t('events.form.dateTime')}
                </label>
                <DateTimePicker
                  selected={watch('event_date')}
                  onChange={(date) => setValue('event_date', date)}
                  minDate={new Date()}
                  error={errors.event_date?.message}
                />
              </div>
            </div>
          </div>

          <div className="flex flex-col md:flex-row justify-end md:space-x-4">
            <button
              type="button"
              onClick={() => navigate('/agenda')}
              className="btn-outline order-2 mt-6 md:mt-0"
              disabled={isSubmitting}
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              className="btn-secondary md:order-2"
              disabled={isSubmitting}
            >
              {isSubmitting ? t('common.saving') : t('forms.createEvent')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CreateEvent;